<script lang="ts">
  import { CommonTexts } from '../services/CommonTexts';
  import Icon from '../components/icons/Icon.svelte';
  import SearchHitBadge from '../components/values/SearchHitBadge.svelte';
  import * as Icons from '@lucide/svelte';
  import { ApiService } from '../api/services/ApiService';
  import { SearchEntries } from '../modules/search/SearchEntries';
  import type { SearchEntry } from '../modules/search/SearchEntries';
  import type { SearchHitData } from '../components/values/values.types';
  import { MenuService } from '../services/MenuService.svelte';
  import { PageService } from '../services/PageService.svelte';
  import { SearchService } from '../services/SearchService';
  import { UserService } from '../services/UserService.svelte';

  /**
   * The search of the header: once the field rests, a dropdown shows the best hit of each module (MD/CLAUDE_SEARCH.md).
   * Choosing one, by mouse or arrows and Enter, opens it; Enter without a choice opens the big search. Both empty the field,
   * the query lives on at the target.
   */
  const ListId = $props.id();
  const IsLoggedIn = $derived(UserService.body.isLoggedIn);
  let Query = $state('');
  let Entries = $state.raw<SearchEntry[]>([]);
  let ActiveIndex = $state(-1);
  let IsOpen = $state(false);
  let Timer: number | undefined;
  /** Only the answer to the latest query counts. */
  let LoadId = 0;

  function getOptionId(index: number): string {
    return `${ListId}-${index.toString()}`;
  }

  function close(): void {
    window.clearTimeout(Timer);
    LoadId++;
    IsOpen = false;
    ActiveIndex = -1;
  }

  function reset(): void {
    close();
    Query = '';
    Entries = [];
  }

  function handleInput(): void {
    window.clearTimeout(Timer);
    ActiveIndex = -1;
    if (Query.trim() === '') {
      close();
      return;
    }
    Timer = window.setTimeout((): void => {
      void load(Query.trim());
    }, SearchService.DelayMs);
  }

  async function load(query: string): Promise<void> {
    const MyId = ++LoadId;
    const Result = await ApiService.search.searchBest(query);
    if (MyId !== LoadId || !Result.isOk || Result.body === undefined) {
      return;
    }
    Entries = SearchEntries.list(Result.body, query);
    ActiveIndex = -1;
    IsOpen = true;
  }

  function choose(entry: SearchEntry): void {
    PageService.set(entry.target);
    reset();
  }

  /** Enter opens the choice, else the big search; an empty query stays here. */
  function submitSearch(event: SubmitEvent): void {
    event.preventDefault();
    const Chosen = IsOpen ? Entries[ActiveIndex] : undefined;
    if (Chosen !== undefined) {
      choose(Chosen);
    } else if (Query.trim() !== '') {
      PageService.set(MenuService.getSearchHref(Query));
      reset();
    }
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      close();
    } else if (IsOpen && Entries.length > 0 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      const Step = event.key === 'ArrowDown' ? 1 : -1;
      // - from -1 (nothing chosen) one slot further round: back to nothing is possible
      ActiveIndex = ((ActiveIndex + 1 + Step + Entries.length + 1) % (Entries.length + 1)) - 1;
    }
  }

  /** A tag in the dropdown is plain text, its badge would be a link inside the link. */
  function getHit(entry: SearchEntry): SearchHitData {
    return entry.hit.tag === undefined ? entry.hit : { text: `#${entry.hit.tag}` };
  }

  function keepFocus(event: MouseEvent): void {
    event.preventDefault();
  }

  $effect(() => {
    return (): void => {
      close();
    };
  });
</script>

<aside class="LayoutSearch">
  {#if IsLoggedIn}
    <form class="searchInput lyBorder lyRounded" role="search" onsubmit={submitSearch}>
      <span class="searchIcon"><Icon icon={Icons.Search} size={1} /></span>
      <input
        type="search"
        name="q"
        role="combobox"
        placeholder={CommonTexts.SearchPlaceholder}
        aria-label={CommonTexts.Search}
        aria-expanded={IsOpen}
        aria-controls={ListId}
        aria-activedescendant={ActiveIndex >= 0 ? getOptionId(ActiveIndex) : undefined}
        autocomplete="off"
        bind:value={Query}
        oninput={handleInput}
        onkeydown={handleKeyDown}
        onblur={close} />
      {#if IsOpen}
        <div id={ListId} class="searchHits lyBorder lyRounded" role="listbox" tabindex="-1" aria-label={CommonTexts.SearchHitsLabel} onmousedown={keepFocus}>
          {#each Entries as entry, index (entry.key)}
            <a
              id={getOptionId(index)}
              class="searchHit"
              class:isActive={index === ActiveIndex}
              role="option"
              aria-selected={index === ActiveIndex}
              tabindex="-1"
              href={entry.target}
              onclick={(event: MouseEvent): void => {
                event.preventDefault();
                choose(entry);
              }}>
              <span class="hitIcon"><Icon icon={entry.icon} size={1.25} /></span>
              <span class="hitName"><SearchHitBadge {...getHit(entry)} /></span>
              <small>{entry.area}</small>
            </a>
          {:else}
            <p class="searchEmpty">{CommonTexts.SearchEmpty}</p>
          {/each}
        </div>
      {/if}
    </form>
  {/if}
</aside>

<style lang="scss">
  @use '../../styles/mixins';

  .LayoutSearch {
    display: none;
    min-width: 0;

    @include mixins.greaterNav() {
      display: flex;
      align-items: center;
      width: auto;
    }

    .searchInput {
      position: relative;
      display: flex;
      align-items: center;
      gap: 0.4em;
      padding: 0.25em 0.6em;
      font-size: calc(var(--font-size) * 1.5);
      max-width: 15rem;
      min-width: 0;

      .searchIcon {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.8em;
        line-height: 1;
        flex-shrink: 0;
      }

      input {
        display: block;
        border: none !important;
        outline: none !important;
        background: transparent;
        padding: 0;
        margin: 0;
        font-size: 0.75em;
        line-height: 1.2;
        min-width: 0;
        width: 100%;
      }
    }

    .searchHits {
      position: absolute;
      top: calc(100% + 0.25em);
      right: 0;
      z-index: 600;
      width: max-content;
      min-width: 100%;
      max-width: min(30rem, 90vw);
      padding: 0.25em;
      font-size: var(--font-size);
      background: var(--layout-background);
      box-shadow: 0 0.5em 1.5em rgb(0 0 0 / 25%);
    }

    .searchHit {
      display: flex;
      align-items: center;
      gap: 0.6em;
      padding: 0.35em 0.5em;
      color: inherit;
      text-decoration: none;
      border-radius: 0.35em;

      &:hover,
      &.isActive {
        background-color: color-mix(in srgb, currentcolor 12%, transparent);
      }

      .hitIcon {
        display: inline-flex;
        flex-shrink: 0;
      }

      .hitName {
        flex: 1;
        min-width: 0;
      }

      small {
        opacity: 0.7;
        white-space: nowrap;
      }
    }

    .searchEmpty {
      margin: 0;
      padding: 0.35em 0.5em;
    }
  }
</style>
